import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import { SavedViewChips } from "@/components/table/SavedViewChips";
import type { DataTableColumn, DataTableFilterConfig } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, ReferenceListItemResource, VendorResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { labelFor } from "@/lib/field-labels";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { useIsPhone } from "@/lib/use-media-query";

import { AddVendorDialog } from "./AddVendorDialog";

/**
 * `/vendors` (Story 16.2) — the real, tenth nav destination's own list
 * screen. Mirrors `TenantsList.tsx`'s own established "shared data table
 * with saved views" shape exactly (spec's own bullet 2): the same
 * `DataTable`/`SavedViewChips`/`AddXDialog`/row-`Remove` shape, parameterized
 * for `VendorResource` instead of `TenantResource`.
 *
 * **Columns and sort (judgement call 3).** name/trade/company name/phone,
 * in the AC's own listed order. `full_name`/`trade`/`company_name` are all
 * real, single-valued columns the backend's own `vendors_repo.
 * SORTABLE_FIELDS` (`app/repo/vendors.py`) actually knows how to sort by —
 * unlike `TenantsList.tsx`'s `current_lease_start_date`/`current_lease_end_
 * date` or `PropertiesList.tsx`'s `tenants`/lease-date columns (Story
 * 15.21/15.24's own `sortable: false` precedent), which are `sortable: false`
 * because they are either bulk-resolved/computed values with no real
 * backend sort support, or a multi-valued, unordered set with no
 * well-defined single sort key. `trade`/`company_name` share neither
 * disqualifying property — a real, single scalar column per row — so both
 * stay sortable (the default), the opposite conclusion from that precedent,
 * reached by applying its own stated reasoning rather than copying its
 * literal `sortable: false`. `mobile_phone`, by contrast, genuinely is
 * *not* in `SORTABLE_FIELDS` server-side (mirrors `tenants_repo.
 * SORTABLE_FIELDS`'s own identical omission) — its column here is `sortable:
 * false` so a click on its header can never issue a `GET /vendors?
 * sort=mobile_phone` the backend would 400 on.
 *
 * `trade`'s own filter `<select>` (VC-16.2-03) is `DataTable`'s native
 * filter mechanism, sourced from a real `GET /reference-list-items?
 * list_key=vendor_trade` fetch — mirrors `DocumentsList.tsx`'s own identical
 * `category`-filter shape (itself mirroring `AddDocumentDialog.tsx`'s
 * `<select>`), parameterized to `vendor_trade` instead of `document_
 * category`.
 */

const FILTERABLE_FIELDS = ["trade"] as const;

const VENDORS_LIST_KEY = "vendors";
const VENDOR_TRADE_LIST_KEY = "vendor_trade";

export function VendorsList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  //: `POST /vendors` is `role=Role.OWNER` (`app/api/vendors.py`'s own module
  //: docstring) — the create affordance is withheld from anyone but a
  //: confirmed Owner, mirroring `TenantsList.tsx`'s identical `isOwner`-gated
  //: "Add tenant" button.
  const isOwner = useIsOwner();
  const [removeTarget, setRemoveTarget] = useState<VendorResource | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = "Vendors — PropDesk";
  }, []);

  const tradesQuery = useQuery({
    queryKey: ["reference-list-items", VENDOR_TRADE_LIST_KEY],
    queryFn: () =>
      apiFetch<Page<ReferenceListItemResource>>(`/reference-list-items?list_key=${VENDOR_TRADE_LIST_KEY}`),
  });
  const tradeOptions = tradesQuery.data?.data ?? [];
  const tradeLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of tradeOptions) {
      map[item.value] = item.label;
    }
    return map;
  }, [tradeOptions]);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["vendors", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<VendorResource>>(`/vendors?${search}`),
    placeholderData: keepPreviousData,
  });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["vendors", "total"],
    queryFn: () => apiFetch<Page<VendorResource>>("/vendors?page_size=1"),
    enabled: hasActiveFilters,
  });

  const filters = useMemo<DataTableFilterConfig[]>(
    () => [
      {
        field: "trade",
        label: labelFor("trade"),
        options: tradeOptions.map((option) => ({ value: option.value, label: option.label })),
      },
    ],
    [tradeOptions],
  );

  function removeRowFromCache(id: string) {
    queryClient.setQueriesData<Page<VendorResource>>({ queryKey: ["vendors"] }, (existing) => {
      if (existing === undefined || !existing.data.some((candidate) => candidate.id === id)) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.filter((candidate) => candidate.id !== id),
        meta: { ...existing.meta, total: Math.max(0, existing.meta.total - 1) },
      };
    });
  }

  /**
   * Deleting a vendor — the AC's own "a real 30-day restore window" (spec
   * Intent), mirroring `TenantsList.tsx`'s identical "Remove" flow: this
   * dialog runs the real, already-audited `DELETE /vendors/{id}` and adds a
   * UI trigger and a confirmation, nothing more.
   */
  async function confirmRemove() {
    if (removeTarget === null) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/vendors/${removeTarget.id}`, { method: "DELETE" });
      removeRowFromCache(removeTarget.id);
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const columns = useMemo<DataTableColumn<VendorResource>[]>(
    () => [
      { key: "full_name" },
      {
        // Ordered ahead of "trade"/"company_name" (matching VendorRecord.tsx's
        // own field order) so it survives DataTable's phone-row
        // columns.slice(1,3) secondary-column rule (columns.ts's own doc
        // comment: phone selection is driven by column order, not a second
        // flag) — the phone number is the one thing this directory exists to
        // surface for someone calling a vendor from their phone.
        key: "mobile_phone",
        sortable: false,
      },
      {
        key: "trade",
        render: (row) => tradeLabelByValue[row.trade] ?? row.trade,
      },
      {
        key: "company_name",
        render: (row) => row.company_name ?? "—",
      },
    ],
    [tradeLabelByValue],
  );

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="text-title font-semibold tracking-title text-ink md:hidden">
            Vendors
          </h1>
          <p className="mt-2 text-body text-ink-secondary">
            Every vendor you call for repairs and maintenance — searchable by name or trade.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {isOwner ? (
            <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
              Add vendor
            </Button>
          ) : null}
        </div>
      </div>
      <SavedViewChips listKey={VENDORS_LIST_KEY} />
      <DataTable
        caption="Vendors"
        itemNamePlural="vendors"
        tint="cyan"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        filters={filters}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        getRowHref={(row) => `/vendors/${row.id}`}
        emptyState={
          isOwner
            ? {
                heading: "No vendors yet.",
                body: "Add a vendor to start building your directory — the people you call for repairs and maintenance.",
                action: { label: "Add vendor", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No vendors yet.",
                body: "Vendors will appear here once an Owner adds one.",
              }
        }
        {...(isOwner
          ? {
              rowActions: (row: VendorResource) => (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`Remove ${row.full_name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveTarget(row);
                  }}
                >
                  Remove
                </Button>
              ),
            }
          : {})}
      />
      {isOwner ? (
        <AddVendorDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/vendors/${id}`)}
        />
      ) : null}
      {removeTarget ? (
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveTarget(null);
              setRemoveError(undefined);
            }
          }}
          title="Remove this vendor?"
          description={`Removing ${removeTarget.full_name} takes their record out of the active list right away. It can be restored for 30 days.`}
          confirmLabel={`Remove ${removeTarget.full_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={headingRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
